<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.inventory'),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:flex space-x-2 justify-between">
            <div>
                <AppButton :href="meta.links.trade_index" element="inertia-link">
                    {{ $tc('messages.gamification_trades', 2) }}
                </AppButton>
            </div>
        </div>

        <Modal :show="showNewCards" @close="showNewCards = false" with-blur dark>
            <div class="grid grid-cols-3 gap-3 md:gap-6 mt-1">
                <div v-for="(card, index) in cards" class="" :title="card.name" :key="index">
                    <img v-if="card.image_path" :src="card.image_path" alt="" class="w-full" />
                    <div class="truncate">
                        {{ card.name }}
                    </div>
                    <div class="flex items-center space-x-2">
                        <CardRarityIndicator :rarity="card.rarity" />
                        <div class="text-gray-600">
                            {{ card.rarity_display }}
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <AppButton as="inertia-link" :href="meta.links.card_collections_index">
                    {{ $tc('messages.card_collections', 2) }}
                </AppButton>
            </div>
        </Modal>

        <EmptyState picture v-if="items.length === 0">
            <template v-slot:picture />
        </EmptyState>
        <AppWell v-else class="mt-5">
            <div class="grid grid-cols-2 lg:grid-cols-6 gap-6">
                <AppCard v-for="item in items" :key="`${item.id}_${item.count}`">
                    <div class="p-1">
                        <img :src="item.image_path" alt="" />
                        <div>
                            {{ item.name }}
                        </div>
                        <div class="text-gray-600 text-sm">
                            {{ item.options_description }}
                        </div>
                        <div class="flex gap-x-2">
                            <div v-if="item.description">
                                <Tippy>
                                    <button>
                                        <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                    </button>
                                    <template #content>
                                        {{ item.description }}
                                    </template>
                                </Tippy>
                            </div>
                            <div v-if="item.uses_stock">
                                <Tippy>
                                    <button>
                                        <IconBox class="w-6 h-6 text-gray-400" v-if="item.stock_amount" />
                                        <IconBoxOff class="w-6 h-6 text-gray-400" v-else />
                                    </button>
                                    <template #content>
                                        {{ item.stock_message }}
                                    </template>
                                </Tippy>
                            </div>
                            <div v-if="item.max_total_uses">
                                <Tippy>
                                    <button>
                                        <IconInfinityOff class="w-6 h-6 text-gray-400" />
                                    </button>
                                    <template #content>
                                        {{ item.max_total_uses_message }}
                                    </template>
                                </Tippy>
                            </div>
                        </div>
                        <div class="flex justify-between items-center mt-1">
                            <div>
                                <BadgePill :color="item.count ? 'indigo' : 'gray'"> &times;{{ item.count }} </BadgePill>
                            </div>
                            <div>
                                <InertiaLinkButton
                                    as="button"
                                    :href="item.action.url"
                                    type="white"
                                    method="post"
                                    v-if="item.action.url && item.can.use"
                                >
                                    {{ item.action.text }}
                                </InertiaLinkButton>
                            </div>
                        </div>
                        <div class="flex justify-between items-center mt-1" v-if="item.can.purchase">
                            <div>
                                <BadgePill color="yellow">
                                    {{ item.price }}
                                </BadgePill>
                            </div>
                            <InertiaLinkButton as="button" :href="item.purchase_url" type="white" method="post">
                                {{ $t('messages.buy') }}
                            </InertiaLinkButton>
                        </div>
                        <div class="flex justify-between items-center mt-1" v-if="item.display_stock !== null">
                            <div class="text-xs">
                                {{ $t('messages.stock_available:') }}
                            </div>
                            <div>
                                <BadgePill :color="item.display_stock === 0 ? 'gray' : 'indigo'">
                                    {{ item.display_stock }}
                                </BadgePill>
                            </div>
                        </div>
                        <div class="flex justify-between items-center mt-1" v-if="item.required_level_description">
                            {{ $t('messages.level_required') }}:
                            {{ item.required_level_description }}
                        </div>
                        <div class="flex justify-between items-center mt-1" v-if="item.extra_actions.count">
                            <button
                                @click="item.panel_open = true"
                                type="button"
                                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <svg
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="w-4 h-4"
                                >
                                    <path
                                        d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                                    ></path>
                                </svg>
                            </button>
                            <SlideOver :show="item.panel_open" @close="item.panel_open = false" :title="item.name">
                                <img :src="item.image_path" alt="" />
                                <SellSlideOverWidget
                                    v-if="item.extra_actions.actions.sell_back"
                                    :action="item.extra_actions.actions.sell_back"
                                    class="mt-3"
                                />
                            </SlideOver>
                        </div>
                        <div class="flex justify-between items-center mt-1" v-if="!item.meetsCharacterType">
                            <span>
                                <svg
                                    class="w-6 h-6 text-red-300"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </span>
                        </div>
                    </div>
                </AppCard>
            </div>
        </AppWell>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import InertiaLinkButton from '@/components/InertiaLinkButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import Modal from '@/components/Modal.vue';
import CardRarityIndicator from '@/components/CardRarityIndicator.vue';
import SlideOver from '@/components/SlideOver.vue';
import SellSlideOverWidget from '@/components/SellSlideOverWidget.vue';
import { Tippy } from 'vue-tippy';
import { IconBox, IconBoxOff, IconInfinityOff, IconInfoCircle } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';

export default {
    components: {
        AppWell,
        AppCard,
        EmptyState,
        PanelPage,
        SlideOver,
        AppButton,
        InertiaLinkButton,
        BadgePill,
        Modal,
        CardRarityIndicator,
        SellSlideOverWidget,
        Tippy,
        IconBox,
        IconBoxOff,
        IconInfinityOff,
        IconInfoCircle,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'items', 'filters', 'cards'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
            showNewCards: false,
        };
    },
    watch: {
        cards: function (cards) {
            if (cards.length > 0) {
                this.showNewCards = true;
            }
        },
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
